import React, { useEffect, useState } from "react";
import { usersApi, formatErr, tokenStore } from "./api";
import { Breadcrumbs } from "./AdminLayout";
import { Plus, X, Save, Trash2 } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const me = tokenStore.user;

  const load = () => {
    usersApi.list().then(setUsers).catch(e => toast.error(formatErr(e.response?.data?.detail)));
    usersApi.roles().then(setRoles).catch(()=>{});
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-users">
      <Toaster richColors position="top-right"/>
      <Breadcrumbs items={["Admin", "Users & Roles"]}/>
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-serif text-3xl text-navy tracking-tight">Users & Roles</h1>
          <p className="text-stone2 text-sm mt-1">Seven healthcare-oriented roles. The <span className="mono">System</span> actor is reserved and cannot log in.</p>
        </div>
        <button data-testid="new-user-btn" onClick={()=>setShowNew(true)}
          className="inline-flex items-center gap-2 bg-navy hover:bg-navy-hover text-white text-sm px-4 py-2 rounded-none">
          <Plus size={14} strokeWidth={1.5}/> Add user
        </button>
      </div>

      <div className="bg-white border border-line overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-clinical">
            <tr className="text-left mono text-[10px] uppercase tracking-widest text-stone2">
              <th className="px-4 py-3 border-b border-line">Name</th>
              <th className="px-4 py-3 border-b border-line">Email</th>
              <th className="px-4 py-3 border-b border-line">Role</th>
              <th className="px-4 py-3 border-b border-line">Status</th>
              <th className="px-4 py-3 border-b border-line">Last login</th>
              <th className="px-4 py-3 border-b border-line text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} data-testid={`row-${u.id}`} className="border-b border-line last:border-0 hover:bg-clinical/40">
                <td className="px-4 py-3 text-navy font-medium">{u.name}</td>
                <td className="px-4 py-3 text-stone2 mono text-xs">{u.email}</td>
                <td className="px-4 py-3"><span className="mono text-xs px-2 py-0.5 bg-prestige-bg text-prestige rounded-full">{u.role_label}</span></td>
                <td className="px-4 py-3"><span className={u.is_active ? "text-[#3F6250] mono text-xs" : "text-stone2 mono text-xs"}>{u.is_active ? "ACTIVE" : "DISABLED"}</span></td>
                <td className="px-4 py-3 mono text-xs text-stone2">{u.last_login_at ? u.last_login_at.slice(0,16).replace('T',' ') : "—"}</td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`edit-${u.id}`} onClick={()=>setEditing(u)} className="mono text-[10px] uppercase tracking-widest text-navy hover:text-terracotta mr-3">Edit</button>
                  {u.id !== me?.id && (
                    <button data-testid={`del-${u.id}`} onClick={async ()=>{
                      if (!window.confirm(`Delete ${u.email}?`)) return;
                      try { await usersApi.delete(u.id); toast.success("User deleted"); load(); }
                      catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
                    }} className="mono text-[10px] uppercase tracking-widest text-[#9F1239] hover:underline">Delete</button>
                  )}
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan="6" className="px-4 py-6 text-center text-stone2">No users yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {showNew && <UserModal roles={roles} onClose={()=>setShowNew(false)} onSaved={()=>{setShowNew(false); load();}} mode="new"/>}
      {editing && <UserModal roles={roles} user={editing} onClose={()=>setEditing(null)} onSaved={()=>{setEditing(null); load();}} mode="edit"/>}

      <div className="mt-8 bg-prestige-bg/40 border border-prestige/30 p-6">
        <div className="mono text-[10px] uppercase tracking-widest text-prestige">Reserved</div>
        <div className="text-navy mt-2"><strong>System</strong> — non-human actor for audit-log entries and scheduled background operations. Never appears in this table, cannot log in, cannot be edited or deleted.</div>
      </div>
    </div>
  );
}

function UserModal({ roles, user, mode, onClose, onSaved }) {
  const [form, setForm] = useState({
    email: user?.email || "", name: user?.name || "",
    role: user?.role || "content_author", password: "", is_active: user?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (mode === "new") {
        await usersApi.create(form);
        toast.success("User created");
      } else {
        const payload = { name: form.name, role: form.role, is_active: form.is_active };
        if (form.password) payload.password = form.password;
        await usersApi.update(user.id, payload);
        toast.success("User updated");
      }
      onSaved();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-navy-deep/70 flex items-start justify-center p-6 overflow-y-auto" data-testid="user-modal">
      <div className="bg-white w-full max-w-lg mt-16">
        <div className="flex items-center justify-between px-6 py-4 border-b border-line">
          <div className="font-serif text-2xl text-navy">{mode === "new" ? "New User" : "Edit User"}</div>
          <button onClick={onClose} data-testid="modal-close"><X size={18} className="text-stone2"/></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <Field label="Email">
            <input required type="email" disabled={mode === "edit"} value={form.email} onChange={e=>set("email", e.target.value)}
              data-testid="modal-email" className="w-full bg-white border border-line px-3 py-2 text-sm disabled:bg-clinical"/>
          </Field>
          <Field label="Name">
            <input required value={form.name} onChange={e=>set("name", e.target.value)} data-testid="modal-name"
              className="w-full bg-white border border-line px-3 py-2 text-sm"/>
          </Field>
          <Field label="Role">
            <select value={form.role} onChange={e=>set("role", e.target.value)} data-testid="modal-role"
              className="w-full bg-white border border-line px-3 py-2 text-sm">
              {roles.map(r => <option key={r.slug} value={r.slug}>{r.label}</option>)}
            </select>
          </Field>
          <Field label={mode === "new" ? "Password" : "New Password (leave blank to keep)"}>
            <input type="password" required={mode === "new"} value={form.password} onChange={e=>set("password", e.target.value)}
              data-testid="modal-password" className="w-full bg-white border border-line px-3 py-2 text-sm"/>
          </Field>
          {mode === "edit" && (
            <label className="flex items-center gap-2 text-sm text-stone2">
              <input type="checkbox" checked={form.is_active} onChange={e=>set("is_active", e.target.checked)} data-testid="modal-active"/>
              Account active
            </label>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-stone2">Cancel</button>
            <button type="submit" disabled={saving} data-testid="modal-save" className="bg-navy hover:bg-navy-hover text-white text-sm px-5 py-2 inline-flex items-center gap-2">
              <Save size={14} strokeWidth={1.5}/>{saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (<label className="block"><div className="mono text-[10px] uppercase tracking-widest text-stone2 mb-1.5">{label}</div>{children}</label>);
}
